/******/ (() => { // webpackBootstrap
class CartQuantity extends HTMLElement {
  constructor() {
    super();
    this.removeButton = this.querySelector('[data-cart-remove-item]');
    this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
    this.input = this.querySelector('[data-cart-quantity-input]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateLineItem = this.updateLineItem.bind(this);
    this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
    this.handleCartErrors = this.handleCartErrors.bind(this);
    this.setLoadingState = this.setLoadingState.bind(this);
    this.cleanGhostElements = this.cleanGhostElements.bind(this);
    this.hideLoadingState = this.hideLoadingState.bind(this);
    this.setRemoveLoadingState = this.setRemoveLoadingState.bind(this);
    this.hideRemoveLoadingState = this.hideRemoveLoadingState.bind(this);
  }

  connectedCallback() {
    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({key: 'component-cart-quantity'});
    }
    this.removeButton?.addEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach(button => button.addEventListener('click', this.handleButtonClick));
    this.input?.addEventListener('change', this.handleInputChange);

    // Watch for ghost elements appearing dynamically
    this.ghostObserver = new MutationObserver(() => this.cleanGhostElements());
    this.ghostObserver.observe(this, { childList: true, subtree: true });
    
    this.cleanGhostElements();
  }

  cleanGhostElements() {
    // Only remove the specific placeholder span that holds the ghost text
    const ghost = this.querySelector('.js-qty__num-placeholder');
    if (ghost) {
      ghost.remove();
    }
    
    // Reset the input visibility without forcing it to 'block' (which breaks layout)
    if (this.input) {
      this.input.style.setProperty('opacity', '1', 'important');
      this.input.style.setProperty('visibility', 'visible', 'important');
      // Using inline-block keeps it on the same line as buttons
      this.input.style.setProperty('display', 'inline-block', 'important');
      this.input.style.setProperty('position', 'relative', 'important');
    }
  }

  handleButtonClick(e) {
    e.preventDefault();
    const previousValue = this.input.value;
    const adjustment = e.currentTarget.name === 'plus' ? 1 : -1;
    this.adjustQuantity(adjustment);

    this.cleanGhostElements();

    if (previousValue !== this.input.value) {
      this.handleQuantityChange(this.input.value);
    }
  }

  adjustQuantity(adjustment) {
    const currentValue = parseInt(this.input.value) || 0;
    const newValue = currentValue + adjustment;
    const min = this.input.min ? parseInt(this.input.min) : 1;
    
    const finalValue = newValue < min ? min : newValue;
    
    this.input.value = finalValue;
    // Sync attribute so theme CSS (which causes ghosting) sees the change
    this.input.setAttribute('value', finalValue);
  }

  handleInputChange(e) {
    e.preventDefault();
    if (isNaN(this.input.value)) this.input.value = 1;
    this.cleanGhostElements();
    this.handleQuantityChange(this.input.value);
  }

  handleQuantityChange(inputValue) {
    const quantity = parseInt(inputValue);
    this.updateCart(this.itemIndex, quantity);
  }

  handleRemoveItem() {
    this.updateCart(this.itemIndex, 0);
  }

  async updateCart(line, quantity) {
    try {
      this.setLoadingState();
      if (quantity === 0) this.setRemoveLoadingState();

      let response = await this.updateLineItem(line, quantity);
      
      if (!response) {
        this.hideLoadingState();
        return;
      }

      if (this.hasGiftWrap) {
        const adjustment = quantity - this.previousQuantity;
        const cart = await this.getCart();
        if (this.getGiftWrapLine(cart) && this.getGiftWrapQuantity(cart)) {
          response = await this.updateLineItem(this.getGiftWrapLine(cart), this.getGiftWrapQuantity(cart) + adjustment);
        }
      }

      if (window.location.pathname === window.routes.cart_url) {
        window.location.reload();
      } else {
        window.eventBus.emit('update:cart:drawer', response);
        this.updateCartCountIndicator();
        this.hideLoadingState();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      this.hideLoadingState();
    }
  }

  async updateLineItem(line, quantity) {
    const isCartPage = window.location.pathname === window.routes.cart_url;
    const itemKey = this.input.getAttribute('id').replace('updates_', '');

    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: isCartPage ? '' : 'cart-drawer' })
      });

      const parsedResponse = await response.json();

      if (!response.ok || parsedResponse.errors) {
        this.handleCartErrors(itemKey, parsedResponse.description || parsedResponse.errors);
        return null;
      }

      const actualQty = parsedResponse.items[line - 1] ? parsedResponse.items[line - 1].quantity : 0;
      if (parseInt(quantity) !== actualQty && actualQty > 0) {
        this.input.value = actualQty;
        this.input.setAttribute('value', actualQty); 
        this.cleanGhostElements();
        this.handleCartErrors(itemKey, `This item has a minimum of ${actualQty}.`);
        return null; 
      }

      return parsedResponse;
    } catch (error) {
      this.handleCartErrors(itemKey, "Update failed.");
      return null;
    }
  }

  handleCartErrors(id, errors) {
    this.hideLoadingState();
    this.hideRemoveLoadingState();
    const errorElement = document.querySelector(`[data-cart-error-id="${id}"]`);
    if (errorElement) {
      errorElement.innerHTML = errors;
      errorElement.style.display = 'block';
    } else {
      alert(errors);
    }
  }

  async getCart() {
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.message}`);
    }
  }

  updateCartCountIndicator() {
    if (!this.cartCountIndicator) return;
    this.getCart().then(cart => {
       if (window.wetheme && window.wetheme.updateCartCount) {
         window.wetheme.updateCartCount(cart);
       }
    });
  }

  getGiftWrapLine(cart) {
    return cart.items.findIndex(item => item.variant_id === this.giftWrapVariantId) + 1 || null;
  }

  getGiftWrapQuantity(cart) {
    return cart.items.find(item => item.variant_id === this.giftWrapVariantId)?.quantity || null;
  }

  setLoadingState() { this.classList.add('is-loading'); }
  hideLoadingState() { this.classList.remove('is-loading'); }
  setRemoveLoadingState() { this.classList.add('is-removing'); }
  hideRemoveLoadingState() { this.classList.remove('is-removing'); }

  get itemIndex() { return parseInt(this.getAttribute('data-item-index')); }
  get previousQuantity() { return parseInt(this.getAttribute('data-item-quantity')); }
  get hasGiftWrap() { return this.getAttribute('data-has-gift-wrap') === "true"; }
  get giftWrapVariantId() { return parseInt(this.getAttribute('data-gift-wrap-variant-id')); }
}

customElements.define('cart-quantity', CartQuantity);
/******/ })()
;